import { Box, IconButton, TextareaAutosize } from '@mui/material';
import { ASSETS } from '../../const/assets';
import { useState, useRef, ChangeEvent } from 'react';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { IAiModel } from '../../api/chat';
import { ProfileSuccessResponse } from '../../api/authencation';

interface ChatInputProps {
  onSendMessage: (message: string, file?: File, url?: string) => void;
  isLoading: boolean;
  currentModel?: IAiModel | null;
  profile?: ProfileSuccessResponse | null;
  aiLoading: boolean;
  handleStopConversation: () => void;
}

function ChatInput({
  onSendMessage,
  isLoading,
  currentModel,
  profile,
  aiLoading,
  handleStopConversation,
}: ChatInputProps) {
  const [message, setMessage] = useState('');
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSendMessage = () => {
    if (message.trim()) {
      if (file) {
        onSendMessage(message, file, selectedImage);
      } else {
        onSendMessage(message);
      }
      setMessage('');
      setSelectedImage('');
    }
  };

  const handleImageUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file size (20MB = 20 * 1024 * 1024 bytes)
    if (file.size > 20 * 1024 * 1024) {
      alert('File is too large. Maximum size is 20MB.');
      return;
    }

    // Convert to WebP using Canvas
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const targetSize = 512;

        // Calculate aspect ratio to maintain proportions
        let width = img.width;
        let height = img.height;
        let offsetX = 0;
        let offsetY = 0;

        // Set dimensions while maintaining aspect ratio
        if (width > height) {
          height = (height / width) * targetSize;
          width = targetSize;
          offsetY = (targetSize - height) / 2;
        } else {
          width = (width / height) * targetSize;
          height = targetSize;
          offsetX = (targetSize - width) / 2;
        }

        canvas.width = targetSize;
        canvas.height = targetSize;
        const ctx = canvas.getContext('2d');

        // Fill with white background
        if (ctx) {
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, targetSize, targetSize);
        }

        // Draw the image centered
        ctx?.drawImage(img, offsetX, offsetY, width, height);

        // Convert to WebP format
        canvas.toBlob(
          (blob) => {
            if (blob) {
              setFile(new File([blob], 'image.webp', { type: 'image/webp' }));
              setSelectedImage(URL.createObjectURL(blob));
            }
          },
          'image/webp',
          0.8 // quality
        );
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const removeSelectedImage = () => {
    setSelectedImage('');
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <Box
      sx={{
        // display: 'flex',
        gap: { xs: 0.5, sm: 1 },
        px: { xs: 0, sm: 4 },
        pb: { xs: 0, sm: 2 },
        position: 'relative',
        maxWidth: '600px',
        width: '100%',
        // mx: 'auto',
      }}
    >
      <input
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        ref={fileInputRef}
        onChange={handleImageUpload}
      />

      {selectedImage && (
        <Box
          sx={{
            mb: 1,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ position: 'relative', width: 'fit-content' }}>
            <img
              src={selectedImage}
              alt="Selected"
              style={{
                maxHeight: '100px',
                maxWidth: '200px',
                objectFit: 'cover',
                borderRadius: '10px',
              }}
            />
            <IconButton
              size="small"
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                background: 'rgba(0,0,0,0.5)',
                color: 'white',
                p: '2px',
                width: '16px',
                height: '16px',
                '&:hover': {
                  background: 'rgba(0,0,0,0.7)',
                },
              }}
              onClick={removeSelectedImage}
            >
              ×
            </IconButton>
          </Box>
        </Box>
      )}

      <TextareaAutosize
        disabled={isLoading}
        minRows={3}
        maxRows={3}
        placeholder="Can I help you with anything?"
        className="text-[#3E3E3E] pr-[30px] pl-[35px] pt-[10px] pb-[10px] md:pr-[35px] md:pl-[50px]"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            handleSendMessage();
            e.preventDefault(); // Prevents adding a new line
          }
        }}
        style={{
          width: '100%',
          borderRadius: '30px',
          boxShadow: '5px 4px 20px 0px #00000021',
          // padding: '10px 35px 10px 50px',
          fontSize: '1rem',
          resize: 'none',
          outline: 'none',
          border: 'none',
        }}
        onFocus={(e) => {
          e.target.style.border = 'none';
        }}
        onBlur={(e) => {
          e.target.style.border = 'none';
        }}
      />
      {currentModel?.type === 'IMAGE_TEXT2TEXT' && (
        <IconButton
          color="primary"
          onClick={handleImageClick}
          disabled={isLoading}
          sx={{
            position: 'absolute',
            left: { xs: '0px', md: '40px' },
            bottom: { xs: '10px', md: '25px' },
            width: 40,
            height: 40,
            cursor: 'pointer',
          }}
        >
          <AddPhotoAlternateIcon />
        </IconButton>
      )}
      {aiLoading ? (
        <div
          onClick={handleStopConversation}
          className="flex items-center justify-center rounded h-5 w-5 bg-red-600 cursor-pointer absolute md:right-[46px] md:bottom-[35px] right-[10px] bottom-[20px]"
        ></div>
      ) : (
        <IconButton
          onClick={handleSendMessage}
          disabled={
            (!message.trim() && !selectedImage) ||
            isLoading ||
            parseInt(`${profile?.credit || '0'}`, 10) <= 0
          }
          sx={{
            position: 'absolute',
            right: { xs: '0px', md: '35px' },
            bottom: { xs: '10px', md: '25px' },
            width: 40,
            height: 40,
          }}
        >
          <img
            src={
              message.trim() &&
              !isLoading &&
              parseInt(`${profile?.credit || '0'}`, 10) > 0
                ? ASSETS.icSend
                : ASSETS.icSendDisabled
            }
            alt="icSend"
          />
        </IconButton>
      )}
    </Box>
  );
}

export default ChatInput;
